// ── Screen Wake Lock ──────────────────────────────────────────────
//
// On a phone the screen locks after ~30s of no touching. When that
// happens the browser may suspend the tab, which for an audio room means
// the WebRTC connection stalls and — worse — the presence heartbeat stops
// and everyone else watches the user disappear from the roster.
//
// The Screen Wake Lock API keeps the display awake while a room is open.
// It is only available in secure contexts and is unsupported on iOS
// Safari, so every call is best-effort: if it fails the room still works,
// the user just has to keep the screen on themselves.
//
// The lock is released automatically by the browser when the page is
// hidden, so it must be re-acquired on visibilitychange.

import { useEffect } from 'react';

interface WakeLockSentinelLike {
  released: boolean;
  release(): Promise<void>;
  addEventListener(type: 'release', listener: () => void): void;
}

interface WakeLockLike {
  request(type: 'screen'): Promise<WakeLockSentinelLike>;
}

function getWakeLock(): WakeLockLike | null {
  const nav = navigator as Navigator & { wakeLock?: WakeLockLike };
  return nav.wakeLock ?? null;
}

/** True when this browser can hold a screen wake lock. */
export function isWakeLockSupported(): boolean {
  return getWakeLock() !== null;
}

/**
 * Hold a screen wake lock while `active` is true.
 *
 * Silently does nothing where unsupported (notably iOS Safari) — callers
 * must not depend on it, only benefit from it.
 */
export function useWakeLock(active: boolean) {
  useEffect(() => {
    if (!active) return;
    const wakeLock = getWakeLock();
    if (!wakeLock) return;

    let sentinel: WakeLockSentinelLike | null = null;
    let cancelled = false;

    const acquire = async () => {
      // Requesting while hidden always rejects; wait for the next
      // visibility change instead of burning a failed request.
      if (document.visibilityState !== 'visible') return;
      try {
        const next = await wakeLock.request('screen');
        if (cancelled) {
          void next.release().catch(() => {});
          return;
        }
        sentinel = next;
      } catch {
        // Denied or unsupported — the room works regardless.
      }
    };

    const onVisibility = () => {
      // The browser drops the lock when the page is hidden, so re-take it
      // when the user comes back.
      if (document.visibilityState === 'visible' && (!sentinel || sentinel.released)) {
        void acquire();
      }
    };

    void acquire();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      if (sentinel && !sentinel.released) {
        void sentinel.release().catch(() => {});
      }
      sentinel = null;
    };
  }, [active]);
}
